import React from 'react';
import { Avatar, IconButton, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SettingsIcon from '@mui/icons-material/Settings';
import { grey } from '@mui/material/colors';
import { useNavigate } from 'react-router-dom';

const NavItem = ({ active, onClick, children, count, icon }) => (
  <button type="button" className={`library-nav-item${active ? ' active' : ''}`} onClick={onClick}>
    {icon}
    <span>{children}</span>
    {count != null ? <span className="library-nav-item-count">{count}</span> : null}
  </button>
);

const LibrarySidebar = ({
  activeSection,
  boardName,
  searchQuery,
  person,
  isOwner,
  userCanvasMapping,
  onNavigateAll,
  onNavigateBoard,
  onNavigateCreate,
  showProfileHeader = false,
  ownerUsernameTooltip = '',
}) => {
  const navigate = useNavigate();

  const mappingBoards = userCanvasMapping?.canvasMappings || [];
  const hashtagBoards = (person?.hashtags || []).map(tag => ({
    name: tag.name,
    color: tag.color,
    activityCount: null,
    screenshotUrl: null,
  }));
  // Prefer mapping metadata (counts / thumbs), but always include every hashtag
  // board so a partial canvasMappings sync cannot hide boards from the sidebar.
  const boards = (() => {
    if (!(isOwner && mappingBoards.length)) return hashtagBoards;
    const seen = new Set();
    const merged = [];
    for (const board of mappingBoards) {
      if (!board?.name || seen.has(board.name)) continue;
      seen.add(board.name);
      merged.push(board);
    }
    for (const board of hashtagBoards) {
      if (!board?.name || seen.has(board.name)) continue;
      seen.add(board.name);
      merged.push(board);
    }
    return merged;
  })();

  const hasNoHashtagBoard = Boolean(
    userCanvasMapping?.canvasMappings?.some(m => m.name === 'no_hashtag')
  );
  const showDefaultBoard = !isOwner || hasNoHashtagBoard;

  return (
    <div className="library-sidebar-inner">
      {showProfileHeader && person ? (
        <div className="library-sidebar-profile">
          <Avatar
            src={person.profileImageUrl}
            className="library-sidebar-profile-avatar"
          />
          <div className="library-sidebar-profile-identity">
            <Tooltip
              title={ownerUsernameTooltip}
              arrow
              placement="bottom"
              disableHoverListener={!isOwner || !ownerUsernameTooltip}
              disableFocusListener={!isOwner || !ownerUsernameTooltip}
              disableTouchListener={!isOwner || !ownerUsernameTooltip}
            >
              <Typography className="library-sidebar-profile-username" component="span">
                @{person.username}
              </Typography>
            </Tooltip>
            {isOwner ? (
              <IconButton
                size="small"
                aria-label="Settings"
                onClick={() => navigate('/settings')}
              >
                <SettingsIcon fontSize="small" />
              </IconButton>
            ) : null}
          </div>
        </div>
      ) : null}

      <div className="library-sidebar-section-label">Collection</div>
      <NavItem
        active={activeSection === 'all' && !boardName && !searchQuery}
        onClick={onNavigateAll}
      >
        All items
      </NavItem>

      <div className="library-sidebar-section-header">
        <div className="library-sidebar-section-label">AI insights</div>
        {isOwner && onNavigateCreate ? (
          <IconButton
            size="small"
            className="library-sidebar-section-add"
            aria-label="Create new memory board"
            onClick={onNavigateCreate}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        ) : null}
      </div>
      {showDefaultBoard ? (
        <NavItem
          active={boardName === 'no_hashtag'}
          onClick={() => onNavigateBoard('no_hashtag')}
          icon={<span className="library-board-dot" style={{ background: grey[500] }} />}
        >
          Default
        </NavItem>
      ) : null}
      {boards.map(board => (
        <NavItem
          key={board.name}
          active={boardName === board.name}
          onClick={() => onNavigateBoard(board.name)}
          count={board.activityCount}
          icon={
            board.screenshotUrl ? (
              <img
                src={board.screenshotUrl}
                alt=""
                className="library-board-thumb"
                loading="lazy"
              />
            ) : (
              <span
                className="library-board-dot"
                style={{ background: board.color || '#6366f1' }}
              />
            )
          }
        >
          {board.name}
        </NavItem>
      ))}
    </div>
  );
};

export default LibrarySidebar;
